"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClearingHouseUser = void 0;
const bn_js_1 = __importDefault(require("bn.js"));
const numericConstants_1 = require("./constants/numericConstants");
const defaultUserAccountSubscriber_1 = require("./accounts/defaultUserAccountSubscriber");
const _1 = require(".");
const addresses_1 = require("./addresses");
class ClearingHouseUser {
    constructor(clearingHouse, authority, accountSubscriber) {
        this.isSubscribed = false;
        this.clearingHouse = clearingHouse;
        this.authority = authority;
        this.accountSubscriber = accountSubscriber;
        this.eventEmitter = this.accountSubscriber.eventEmitter;
    }
    static from(clearingHouse, authority) {
        const accountSubscriber = new defaultUserAccountSubscriber_1.DefaultUserAccountSubscriber(clearingHouse.program, authority);
        return new ClearingHouseUser(clearingHouse, authority, accountSubscriber);
    }
    /**
     * Subscribe to ClearingHouseUser state accounts
     * @returns SusbcriptionSuccess result
     */
    subscribe() {
        return __awaiter(this, void 0, void 0, function* () {
            // Clearing house should already be subscribed, but await for the subscription just incase to avoid race condition
            yield this.clearingHouse.subscribe();
            this.isSubscribed = yield this.accountSubscriber.subscribe();
            return this.isSubscribed;
        });
    }
    unsubscribe() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.accountSubscriber.unsubscribe();
            this.isSubscribed = false;
        });
    }
    getUserAccount() {
        return this.accountSubscriber.getUserAccount();
    }
    getUserPositionsAccount() {
        return this.accountSubscriber.getUserPositionsAccount();
    }
    /**
     * Gets the user's current position for a given market
     * @param marketIndex
     * @returns userPosition
     */
    getUserPosition(marketIndex) {
        var _a;
        return ((_a = this.getUserPositionsAccount().positions.find((position) => position.marketIndex.eq(marketIndex))) !== null && _a !== void 0 ? _a : {
            baseAssetAmount: numericConstants_1.ZERO,
            lastCumulativeFundingRate: numericConstants_1.ZERO,
            marketIndex,
            quoteAssetAmount: numericConstants_1.ZERO,
        });
    }
    getUserAccountPublicKey() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.userAccountPublicKey) {
                return this.userAccountPublicKey;
            }
            this.userAccountPublicKey = yield addresses_1.getUserAccountPublicKey(this.clearingHouse.program.programId, this.authority);
            return this.userAccountPublicKey;
        });
    }
    exists() {
        return __awaiter(this, void 0, void 0, function* () {
            const userAccountPublicKey = yield this.getUserAccountPublicKey();
            const userAccountRPCResponse = yield this.clearingHouse.connection.getParsedAccountInfo(userAccountPublicKey);
            return userAccountRPCResponse.value !== null;
        });
    }
    /**
     * calculates Buying Power = FC * MAX_LEVERAGE
     * @returns : Precision QUOTE_PRECISION
     */
    getBuyingPower() {
        return this.getFreeCollateral()
            .mul(this.getMaxLeverage('Initial'))
            .div(numericConstants_1.TEN_THOUSAND);
    }
    /**
     * calculates Free Collateral = (TC - TPV) * MAX_LEVERAGE
     * @returns : Precision QUOTE_PRECISION
     */
    getFreeCollateral() {
        return this.getTotalCollateral().sub(this.getTotalPositionValue()
            .mul(numericConstants_1.TEN_THOUSAND)
            .div(this.getMaxLeverage('Initial')));
    }
    /**
     * calculates unrealized position price pnl
     * @returns : Precision QUOTE_PRECISION
     */
    getUnrealizedPNL(withFunding) {
        return this.getUserPositionsAccount().positions.reduce((pnl, marketPosition) => {
            const market = this.clearingHouse.getMarket(marketPosition.marketIndex);
            return pnl.add(_1.calculatePositionPNL(market, marketPosition, withFunding));
        }, numericConstants_1.ZERO);
    }
    /**
     * calculates unrealized funding payment pnl
     * @returns : Precision QUOTE_PRECISION
     */
    getUnrealizedFundingPNL() {
        return this.getUserPositionsAccount().positions.reduce((pnl, marketPosition) => {
            const market = this.clearingHouse.getMarket(marketPosition.marketIndex);
            return pnl.add(_1.calculatePositionFundingPNL(market, marketPosition));
        }, numericConstants_1.ZERO);
    }
    /**
     * calculates TotalCollateral: collateral + unrealized pnl
     * @returns : Precision QUOTE_PRECISION
     */
    getTotalCollateral() {
        var _a;
        return ((_a = this.getUserAccount().collateral.add(this.getUnrealizedPNL(true))) !== null && _a !== void 0 ? _a : new bn_js_1.default(0));
    }
    /**
     * calculates sum of position value across all positions
     * @returns : Precision QUOTE_PRECISION
     */
    getTotalPositionValue() {
        return this.getUserPositionsAccount().positions.reduce((positionValue, marketPosition) => {
            const market = this.clearingHouse.getMarket(marketPosition.marketIndex);
            return positionValue.add(_1.calculateBaseAssetValue(market, marketPosition));
        }, numericConstants_1.ZERO);
    }
    /**
     * calculates position value from closing 100%
     * @returns : Precision QUOTE_PRECISION
     */
    getPositionValue(marketIndex) {
        const userPosition = this.getUserPosition(marketIndex);
        const market = this.clearingHouse.getMarket(userPosition.marketIndex);
        return _1.calculateBaseAssetValue(market, userPosition);
    }
    getPositionSide(currentPosition) {
        if (currentPosition.baseAssetAmount.gt(numericConstants_1.ZERO)) {
            return _1.PositionDirection.LONG;
        }
        else if (currentPosition.baseAssetAmount.lt(numericConstants_1.ZERO)) {
            return _1.PositionDirection.SHORT;
        }
        else {
            return undefined;
        }
    }
    /**
     * calculates average exit price for closing 100% of position
     * @returns : Precision MARK_PRICE_PRECISION
     */
    getPositionEstimatedExitPrice(position, amountToClose) {
        const market = this.clearingHouse.getMarket(position.marketIndex);
        if (amountToClose) {
            if (amountToClose.eq(numericConstants_1.ZERO)) {
                return _1.calculateMarkPrice(market);
            }
            position = {
                baseAssetAmount: amountToClose,
                lastCumulativeFundingRate: position.lastCumulativeFundingRate,
                marketIndex: position.marketIndex,
                quoteAssetAmount: position.quoteAssetAmount,
            };
        }
        const baseAssetValue = _1.calculateBaseAssetValue(market, position);
        if (position.baseAssetAmount.eq(numericConstants_1.ZERO)) {
            return numericConstants_1.ZERO;
        }
        return baseAssetValue
            .mul(numericConstants_1.AMM_TO_QUOTE_PRECISION_RATIO)
            .mul(numericConstants_1.MARK_PRICE_PRECISION)
            .div(position.baseAssetAmount.abs());
    }
    /**
     * calculates current user leverage across all positions
     * @returns : Precision TEN_THOUSAND
     */
    getLeverage() {
        const totalCollateral = this.getTotalCollateral();
        const totalPositionValue = this.getTotalPositionValue();
        if (totalPositionValue.eq(numericConstants_1.ZERO) && totalCollateral.eq(numericConstants_1.ZERO)) {
            return numericConstants_1.ZERO;
        }
        return totalPositionValue.mul(numericConstants_1.TEN_THOUSAND).div(totalCollateral);
    }
    /**
     * calculates max allowable leverage exceeding hitting requirement category
     * @params category {Initial, Partial, Maintenance}
     * @returns : Precision TEN_THOUSAND
     */
    getMaxLeverage(category) {
        const chState = this.clearingHouse.getStateAccount();
        let marginRatioCategory;
        switch (category) {
            case 'Initial':
                marginRatioCategory = chState.marginRatioInitial;
                break;
            case 'Maintenance':
                marginRatioCategory = chState.marginRatioMaintenance;
                break;
            case 'Partial':
                marginRatioCategory = chState.marginRatioPartial;
                break;
            default:
                marginRatioCategory = chState.marginRatioInitial;
                break;
        }
        const maxLeverage = numericConstants_1.TEN_THOUSAND.mul(numericConstants_1.TEN_THOUSAND).div(marginRatioCategory);
        return maxLeverage;
    }
    /**
     * calculates margin ratio: total collateral / |total position value|
     * @returns : Precision TEN_THOUSAND
     */
    getMarginRatio() {
        const totalPositionValue = this.getTotalPositionValue();
        if (totalPositionValue.eq(numericConstants_1.ZERO)) {
            return numericConstants_1.BN_MAX;
        }
        return this.getTotalCollateral().mul(numericConstants_1.TEN_THOUSAND).div(totalPositionValue);
    }
    canBeLiquidated() {
        const marginRatio = this.getMarginRatio();
        const canLiquidate = marginRatio.lte(numericConstants_1.PARTIAL_LIQUIDATION_RATIO);
        return [canLiquidate, marginRatio];
    }
    /**
     * Checks if any user position cumulative funding differs from respective market cumulative funding
     * @returns
     */
    needsToSettleFundingPayment() {
        const marketsAccount = this.clearingHouse.getMarketsAccount();
        for (const userPosition of this.getUserPositionsAccount().positions) {
            if (userPosition.baseAssetAmount.eq(numericConstants_1.ZERO)) {
                continue;
            }
            const market = marketsAccount.markets[userPosition.marketIndex.toNumber()];
            if (market.amm.cumulativeFundingRateLong.eq(userPosition.lastCumulativeFundingRate) ||
                market.amm.cumulativeFundingRateShort.eq(userPosition.lastCumulativeFundingRate)) {
                continue;
            }
            return true;
        }
        return false;
    }
    /**
     * Calculate the liquidation price of a position, with optional parameter to calculate the liquidation price after a trade
     * @param targetMarket
     * @param positionBaseSizeChange // change in position size to calculate liquidation price for : Precision 10^13
     * @param partial
     * @returns Precision : MARK_PRICE_PRECISION
     */
    liquidationPrice(targetMarket, positionBaseSizeChange = numericConstants_1.ZERO, partial = false) {
        // +/-(margin_ratio-liq_ratio) * price_now = price_liq
        // todo: margin_ratio is not symmetric on price action (both numer and denom change)
        // margin_ratio = collateral / base_asset_value
        /* example: assume BTC price is $40k (examine 10% up/down)
        
        if 10k deposit and levered 10x short BTC => BTC up $400 means:
        1. higher base_asset_value (+$4k)
        2. lower collateral (-$4k)
        3. (10k - 4k)/(100k + 4k) => 6k/104k => .0576

        for 10x long, BTC down $400:
        3. (10k - 4k) / (100k - 4k) = 6k/96k => .0625 */
        const currentPrice = _1.calculateMarkPrice(this.clearingHouse.getMarket(targetMarket.marketIndex));
        const totalCollateralUSDC = this.getTotalCollateral();
        // calculate the total position value ignoring any value from the target market of the trade
        const totalCurrentPositionValueIgnoringTargetUSDC = this.getTotalPositionValueExcludingMarket(targetMarket.marketIndex);
        const currentMarketPosition = this.getUserPosition(targetMarket.marketIndex);
        const currentMarketPositionBaseSize = currentMarketPosition
            ? currentMarketPosition.baseAssetAmount
            : numericConstants_1.ZERO;
        // calculate position for current market after trade
        const proposedMarketPosition = {
            marketIndex: targetMarket.marketIndex,
            baseAssetAmount: currentMarketPositionBaseSize.add(positionBaseSizeChange),
            lastCumulativeFundingRate: new bn_js_1.default(0),
            quoteAssetAmount: new bn_js_1.default(0),
        };
        const market = this.clearingHouse.getMarket(proposedMarketPosition.marketIndex);
        const proposedMarketPositionValueUSDC = _1.calculateBaseAssetValue(market, proposedMarketPosition);
        // total position value after trade
        const targetTotalPositionValueUSDC = totalCurrentPositionValueIgnoringTargetUSDC.add(proposedMarketPositionValueUSDC);
        // if the position value after the trade is less than total collateral, there is no liq price
        if (targetTotalPositionValueUSDC.lte(totalCollateralUSDC)) {
            return new bn_js_1.default(-1);
        }
        // proportion of proposed market position to overall position
        // const marketProportion = proposedMarketPositionValueUSDC
        // 	.mul(TEN_THOUSAND)
        // 	.div(targetTotalPositionValueUSDC);
        // get current margin ratio based on current collateral and proposed total position value
        let marginRatio;
        if (targetTotalPositionValueUSDC.eq(numericConstants_1.ZERO)) {
            marginRatio = numericConstants_1.BN_MAX;
        }
        else {
            marginRatio = totalCollateralUSDC
                .mul(numericConstants_1.TEN_THOUSAND)
                .div(targetTotalPositionValueUSDC);
        }
        let liqRatio = numericConstants_1.FULL_LIQUIDATION_RATIO;
        if (partial) {
            liqRatio = numericConstants_1.PARTIAL_LIQUIDATION_RATIO;
        }
        // sign of position in current market after the trade
        const baseAssetSignIsNeg = proposedMarketPosition.baseAssetAmount.isNeg();
        // console.log(
        // 	convertToNumber(currentPrice),
        // convertToNumber(liqRatio),
        // convertToNumber(marginRatio),
        // convertToNumber(marketProportion),
        // );
        // // if the user is long, then the liq price is the currentPrice multiplied by liqRatio/marginRatio (how many multiples lower does the current marginRatio have to go to reach the liqRatio), multiplied by the fraction of the proposed total position value that this market will take up
        // if (!baseAssetSignIsNeg) {
        // 	liqPrice = currentPrice
        // 		.mul(liqRatio)
        // 		.div(marginRatio)
        // 		.mul(marketProportion)
        // 		.div(TEN_THOUSAND);
        // } else {
        // 	// if the user is short, it's the reciprocal of the above
        // 	liqPrice = currentPrice
        // 		.mul(marginRatio)
        // 		.div(liqRatio)
        // 		.mul(TEN_THOUSAND)
        // 		.div(marketProportion);
        // }
        let pctChange = marginRatio.abs().sub(liqRatio);
        // if user is short, higher price is liq
        if (baseAssetSignIsNeg) {
            pctChange = pctChange.add(numericConstants_1.TEN_THOUSAND);
        }
        else {
            if (numericConstants_1.TEN_THOUSAND.lte(pctChange)) {
                // no liquidation price, position is a fully/over collateralized long
                // handle as NaN on UI
                return new bn_js_1.default(-1);
            }
            pctChange = numericConstants_1.TEN_THOUSAND.sub(pctChange);
        }
        const liqPrice = currentPrice.mul(pctChange).div(numericConstants_1.TEN_THOUSAND);
        return liqPrice;
    }
    /**
     * Calculates the estimated liquidation price for a position after closing a quote amount of the position.
     * @param positionMarketIndex
     * @param closeQuoteAmount
     * @returns : Precision MARK_PRICE_PRECISION
     */
    liquidationPriceAfterClose(positionMarketIndex, closeQuoteAmount) {
        const currentPosition = this.getUserPosition(positionMarketIndex);
        const closeBaseAmount = currentPosition.baseAssetAmount
            .mul(closeQuoteAmount)
            .div(currentPosition.quoteAssetAmount)
            .add(currentPosition.baseAssetAmount
            .mul(closeQuoteAmount)
            .mod(currentPosition.quoteAssetAmount))
            .neg();
        return this.liquidationPrice({
            marketIndex: positionMarketIndex,
        }, closeBaseAmount);
    }
    /**
     * Get the maximum trade size for a given market, taking into account the user's current leverage, positions, collateral, etc.
     * @param marketIndex
     * @param tradeSide
     * @param userMaxLeverageSetting - leverage : Precision TEN_THOUSAND
     * @returns tradeSizeAllowed : Precision QUOTE_PRECISION
     */
    getMaxTradeSizeUSDC(targetMarketIndex, tradeSide, userMaxLeverageSetting) {
        // inline function which get's the current position size on the opposite side of the target trade
        const getOppositePositionValueUSDC = () => {
            if (!currentPosition)
                return numericConstants_1.ZERO;
            const side = tradeSide === _1.PositionDirection.SHORT ? 'short' : 'long';
            if (side === 'long' && (currentPosition === null || currentPosition === void 0 ? void 0 : currentPosition.baseAssetAmount.isNeg())) {
                return this.getPositionValue(targetMarketIndex);
            }
            else if (side === 'short' &&
                !(currentPosition === null || currentPosition === void 0 ? void 0 : currentPosition.baseAssetAmount.isNeg())) {
                return this.getPositionValue(targetMarketIndex);
            }
            return numericConstants_1.ZERO;
        };
        const currentPosition = this.getUserPosition(targetMarketIndex);
        // get current leverage
        const currentLeverage = this.getLeverage();
        // remaining leverage
        // let remainingLeverage = userMaxLeverageSetting;
        const remainingLeverage = bn_js_1.default.max(userMaxLeverageSetting.sub(currentLeverage), numericConstants_1.ZERO);
        // get total collateral
        const totalCollateral = this.getTotalCollateral();
        // position side allowed based purely on current leverage
        let maxPositionSize = remainingLeverage
            .mul(totalCollateral)
            .div(numericConstants_1.TEN_THOUSAND);
        // add any position we have on the opposite side of the current trade, because we can "flip" the size of this position without taking any extra leverage.
        const oppositeSizeValueUSDC = getOppositePositionValueUSDC();
        maxPositionSize = maxPositionSize.add(oppositeSizeValueUSDC);
        // subtract oneMillionth of maxPositionSize
        // => to avoid rounding errors when taking max leverage
        const oneMilli = maxPositionSize.div(numericConstants_1.QUOTE_PRECISION);
        return maxPositionSize.sub(oneMilli);
    }
    // TODO - should this take the price impact of the trade into account for strict accuracy?
    /**
     * Returns the leverage ratio for the account after adding (or subtracting) the given quote size to the given position
     * @param targetMarketIndex
     * @param positionMarketIndex
     * @param tradeQuoteAmount
     * @returns leverageRatio : Precision TEN_THOUSAND
     */
    accountLeverageRatioAfterTrade(targetMarketIndex, tradeQuoteAmount, tradeSide) {
        const currentPosition = this.getUserPosition(targetMarketIndex);
        let currentPositionQuoteAmount = currentPosition.quoteAssetAmount;
        const currentSide = currentPosition.baseAssetAmount.isNeg()
            ? _1.PositionDirection.SHORT
            : _1.PositionDirection.LONG;
        if (currentSide === _1.PositionDirection.SHORT)
            currentPositionQuoteAmount = currentPositionQuoteAmount.neg();
        if (tradeSide === _1.PositionDirection.SHORT)
            tradeQuoteAmount = tradeQuoteAmount.neg();
        const currentMarketPositionAfterTrade = currentPositionQuoteAmount
            .add(tradeQuoteAmount)
            .abs();
        const totalPositionAfterTradeExcludingTargetMarket = this.getTotalPositionValueExcludingMarket(targetMarketIndex);
        return currentMarketPositionAfterTrade
            .add(totalPositionAfterTradeExcludingTargetMarket)
            .abs()
            .mul(numericConstants_1.TEN_THOUSAND)
            .div(this.getTotalCollateral());
    }
    /**
     * Calculates how much fee will be taken for a given sized trade
     * @param quoteAmount
     * @returns feeForQuote : Precision QUOTE_PRECISION
     */
    calculateFeeForQuoteAmount(quoteAmount) {
        const feeStructure = this.clearingHouse.getStateAccount().feeStructure;
        return quoteAmount
            .mul(feeStructure.feeNumerator)
            .div(feeStructure.feeDenominator);
    }
    /**
     * Get the total position value, excluding any position coming from the given target market
     * @param marketToIgnore
     * @returns positionValue : Precision QUOTE_PRECISION
     */
    getTotalPositionValueExcludingMarket(marketToIgnore) {
        const currentMarketPosition = this.getUserPosition(marketToIgnore);
        let currentMarketPositionValueUSDC = numericConstants_1.ZERO;
        if (currentMarketPosition) {
            const market = this.clearingHouse.getMarket(currentMarketPosition.marketIndex);
            currentMarketPositionValueUSDC = _1.calculateBaseAssetValue(market, currentMarketPosition);
        }
        return this.getTotalPositionValue().sub(currentMarketPositionValueUSDC);
    }
}
exports.ClearingHouseUser = ClearingHouseUser;
